import React from 'react'
import { Dropdown } from 'react-bootstrap'
import FormError from '../../../shared/components/errors/FormError';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import { useDeleteRoomMaintenance } from '../../../services/room-maintenance/hooks/useDeleteRoomMaintenancen';
import { useQueryClient } from '@tanstack/react-query';
import { IndexRoomMaintenanceResponseType, RoomMaintenanceType } from '../../../services/room-maintenance/response/IndexRoomMaintenanceResponse';
import { toast } from 'sonner';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';

interface RoomMaintenanceTableComponentProps {
    data: IndexRoomMaintenanceResponseType | undefined;
    isLoading: boolean;
    isError: boolean;
    onUpdate: (id: string) => void;
    onPageChange: (page: number) => void;
}

const RoomMaintenanceTableComponent: React.FC<RoomMaintenanceTableComponentProps> = ({
    data,
    isLoading,
    isError,
    onUpdate,
    onPageChange
}) => {

    const { mutate, isPending } = useDeleteRoomMaintenance();

    const queryClient = useQueryClient();

    const onRemove = (data: RoomMaintenanceType) => {
        toast.loading('Removing room maintenance...', { id: 'remove-room-maintenance' });

        mutate({ id: data.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['room-maintenance'].includes(key as string))
                });

                toast.success('Room maintenance removed successfully', { id: 'remove-room-maintenance' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove room maintenance', { id: 'remove-room-maintenance' });
            }
        });
    }

    if (isError) return <FormError />;
    if (isLoading) return <FormSpinner />;

    return (
        <>
            {data && (
                <div className="table-wrapper position-relative table-responsive">
                    <table className="table table-hover table-vcenter">
                        <thead>
                            <tr>
                                <th>Room</th>
                                <th>Sub Room</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Note</th>
                                <th>Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!data.data || data.data.length === 0) ? (
                                <tr>
                                    <td colSpan={13} className="text-center">No data found</td>
                                </tr>
                            ) : (
                                data.data.map((maintenance, index) => (
                                    <React.Fragment key={index}>
                                        <tr>
                                            <td>{maintenance.room_type.name}</td>
                                            <td>{maintenance.room.room_number}</td>
                                            <td>{maintenance.checkin_date}</td>
                                            <td>{maintenance.checkout_date}</td>
                                            <td>{maintenance.note}</td>
                                            <td>{maintenance.status}</td>
                                            <td className="text-end">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                        Actions
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                        {/* <Dropdown.Item href="#" onClick={(e) => handleViewReservation(e, reservation.id)}>View</Dropdown.Item> */}
                                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_update']} asComponent>
                                                            <ReadOnlyGuard mode="hide">
                                                                <Dropdown.Item href="#" onClick={() => onUpdate(maintenance.id)}>Edit</Dropdown.Item>
                                                            </ReadOnlyGuard>
                                                        </RequireAuth>
                                                        {/* <Dropdown.Item href="#" onClick={(e) => handleAssignRoom(e, reservation)}>Assign Room</Dropdown.Item> */}
                                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_delete']} asComponent>
                                                            <ReadOnlyGuard mode="hide">
                                                                <Dropdown.Item href="#" onClick={() => onRemove(maintenance)} disabled={isPending}>Remove</Dropdown.Item>
                                                            </ReadOnlyGuard>
                                                        </RequireAuth>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                    <div className='mt-3'>
                        {data.data && data.pagination && (
                            <PaginationComponent
                                totalPages={data.pagination.last_page}
                                currentPage={data.pagination.current_page}
                                onPageChange={onPageChange}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default RoomMaintenanceTableComponent